import type React from 'react';
import { useEffect } from 'react';

import { useFormContext } from 'react-hook-form';
import type { ZodType } from 'zod';

import { Alert, Button, Text } from '@change/ds-core-ui';
import { Checkbox, Form, TextArea, TextField } from '@change/ds-forms';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import type { ContractFormValues } from '../../hooks/useContractForm';

type Props = {
	schema: ZodType<ContractFormValues>;
	defaultValues: ContractFormValues;
	submitError: boolean;
	onSubmit: (values: ContractFormValues) => Promise<void>;
};

/**
 * Renders the petition-idea textarea and the "not sure" checkbox that disables it and clears its
 * value the moment the box is checked, while leaving `petitionIdea` validation untouched (it stays
 * optional either way).
 *
 * Own component (rather than inlined in the `Form` render-prop callback) so that `useFormContext`
 * and `useEffect` are scoped to a real child fiber mounted under the DS `Form`'s `FormProvider` —
 * same pattern as `TextFieldWithCounter` in the endorsements form.
 */
const PetitionIdeaField: React.FC = () => {
	const { translate } = useI18n();
	const { watch, setValue } = useFormContext<ContractFormValues>();
	const notSure = watch('notSure');

	useEffect(() => {
		if (notSure) {
			setValue('petitionIdea', '', { shouldValidate: false });
		}
	}, [notSure, setValue]);

	return (
		<>
			<TextField
				input={<TextArea />}
				size="large"
				name="petitionIdea"
				label={translate('corgi.ambassadors.modal.petition_idea_label')}
				placeholder={translate('corgi.ambassadors.modal.petition_idea_placeholder')}
				isDisabled={notSure}
			/>
			<Checkbox name="notSure">
				<Translate value="corgi.ambassadors.modal.not_sure_label" />
			</Checkbox>
		</>
	);
};

export const ContractForm: React.FC<Props> = ({ schema, defaultValues, submitError, onSubmit }) => {
	const { translate } = useI18n();

	const optional = translate('corgi.ambassadors.modal.optional_suffix');

	return (
		<Form<ContractFormValues>
			className="w-full"
			schema={schema}
			defaultValues={defaultValues}
			onSubmit={onSubmit}
			data-qa="ambassadors-contract-form"
		>
			{({ formState: { isSubmitting } }) => (
				<div className="flex flex-col gap-4">
					<TextField
						type="text"
						size="large"
						name="name"
						label={translate('corgi.ambassadors.modal.name_label')}
						placeholder={translate('corgi.ambassadors.modal.name_placeholder')}
						autoComplete="name"
						validationBehavior="aria"
						isRequired
					/>
					{/*
					 * Deliberately `text` rather than `email`: the DS `Form` renders react-aria's `Form`,
					 * which leaves native constraint validation on, and a browser-rejected address aborts
					 * submit before the resolver runs — no field error, and the browser's own message is
					 * hardcoded English. `inputMode` keeps the email keyboard on touch devices.
					 */}
					<TextField
						type="text"
						inputMode="email"
						size="large"
						name="email"
						label={translate('corgi.ambassadors.modal.email_label')}
						placeholder={translate('corgi.ambassadors.modal.email_placeholder')}
						autoComplete="email"
						supportingText={translate('corgi.ambassadors.modal.email_help')}
						validationBehavior="aria"
						isRequired
					/>
					<TextField
						type="text"
						size="large"
						name="handle"
						label={translate('corgi.ambassadors.modal.handle_label')}
						placeholder={translate('corgi.ambassadors.modal.handle_placeholder')}
						supportingText={translate('corgi.ambassadors.modal.handle_help')}
						validationBehavior="aria"
						isRequired
					/>
					<PetitionIdeaField />
					<TextField
						type="text"
						size="large"
						name="videoLink"
						label={translate('corgi.ambassadors.modal.video_link_label', { optional })}
						placeholder={translate('corgi.ambassadors.modal.video_link_placeholder')}
					/>
					<Button
						type="submit"
						variant="primary"
						size="large"
						className="w-full"
						disabled={isSubmitting}
						data-qa="ambassadors-contract-form-submit"
					>
						<Translate value="corgi.ambassadors.modal.submit_button" />
					</Button>
					{submitError && (
						<Alert variant="error" data-qa="ambassadors-contract-form-error">
							<Translate value="corgi.ambassadors.modal.error_message" />
						</Alert>
					)}
					<Text as="p" size="small" className="text-typography-weak text-center">
						<Translate value="corgi.ambassadors.modal.fineprint" />
					</Text>
				</div>
			)}
		</Form>
	);
};
